<template>
  <div v-if="status !== null">
    <plain-form v-if="isStatusReissueRequest">
      <p>
        再発行中のため、ダウンロードできません。
      </p>
    </plain-form>
    <download-form v-else-if="canDownload" :can-back="canBack()" @back="toViewer">
      <div v-if="isValidPeriod">
        <p>
          ご依頼の発行が完了しました。<br>
          <span v-if="isGuest">通知したパスワードを入力後、</span>下記のボタンからダウンロードしてください。<br><br>
        </p>
        <file-download-form-item :issues="issues" :with-password="isGuest" :error-message="errors.password?.[0]"
                                 v-model="password" @download="doDownload"
        />
      </div>
      <div v-else>
        <p>
          ダウンロード期間が過ぎたため、発行物のダウンロードはできません。<br>
        </p>
      </div>
      <div v-if="canReissue">
        <div class="mt-5">
          再発行を依頼する場合は「再発行依頼」からご依頼ください。
          <p class="pt-2">※再発行依頼を申請しますと、いま発行されています発行物のダウンロードができなくなります。</p>
        </div>
      </div>
      <template #expansion-buttons-right>
        <new-window-button v-if="canDownloadReciept" text="領収書発行" :window-url="getPrintPreviewUrl()" :window-width="980" :window-height="644" @click="updateStatusHandler($constant.RECEIPT_STATUS.RECEIPT_STATUS_ISSUED)" />
        <new-window-button v-if="canRedownloadReciept" text="領収書再発行" :window-url="getPrintPreviewUrl()" :window-width="980" :window-height="644" @click="updateStatusHandler($constant.RECEIPT_STATUS.RECEIPT_STATUS_REISSUE_DONE)" />
        <base-button class="btn-dark" v-if="canRequestReissueReceipt" text="領収書再発行依頼" @click="updateStatusHandler($constant.RECEIPT_STATUS.RECEIPT_STATUS_REISSUE_REQUEST)" />
        <router-button v-if="canReissue" text="再発行依頼" :to="{name: reissueRouteName, params: {id: issuanceRequestId}}" />
      </template>
    </download-form>
    <plain-form v-else>
      <p>ダウンロード可能な発行物がありません。</p>
    </plain-form>
    <teleport to="body">
      <show-error-upload-modal ref="showErrorModal" />
      <edit-confirm-modal-with-input-area first-description-text="理由を入力して更新ボタンを押下してください。"
                                          second-description-text="入力した内容は管理会社に通知されます。"
                                          input-text-label="理由" :text-max-length="5000"
                                          :required="true" @beforeUpdate="setReceiptReason" 
                                          @update="doReceiptRequest()" 
                                          ref="confirmReceiptModal"
                                          :default-value="''"
      />
    </teleport>
  </div>
</template>

<script>
import {mapState} from "vuex";
import AddonLoadPageEvent from '@/mixin/AddonLoadPageEvent'
import DownloadForm from '@/views/templates/forms/DownloadForm'
import FileDownloadFormItem from '@/views/molecules/formItems/FileDownloadFormItem'
import RouterButton from '@/views/molecules/buttons/RouterButton'
import PlainForm from '@/views/templates/forms/PlainForm'
import NewWindowButton from '@/views/molecules/buttons/NewWindowButton';
import BaseButton from '@/views/atoms/BaseButton';
import ShowErrorUploadModal from '@/views/molecules/modals/ShowErrorUploadModal';
import EditConfirmModalWithInputArea from "@/views/molecules/modals/EditConfirmModalWithInputArea";

export default {
  name: 'IssuanceRequestDownload',
  components: {
    DownloadForm,
    FileDownloadFormItem,
    RouterButton,
    PlainForm,
    NewWindowButton,
    BaseButton,
    EditConfirmModalWithInputArea,
    ShowErrorUploadModal,
  },
  mixins: [AddonLoadPageEvent],
  inject: [
    '$constant',
    '$moment',
  ],
  computed: {
    ...mapState('issuance_request_download', {
      id: state => state.data.id,
      status: state => state.data.status,
      issue_expire_date: state => state.data.issue_expire_date,
      issues: state => state.data.issues,
      errors: state => state.errors,
      reissuePossibleType: state => state.data.building.management_company.reissue_possible_type,
      receipt_issued_status: state => state.data.receipt_issued_status,
      payment_method: state => state.data.payment_method,
      use_receipt: state => state.data.building.management_company.use_receipt,
      billing_price: state => state.data.billing_price,
    }),
    ...mapState('account', {
      isGuest: state => state.is_guest,
      guestIssuanceRequestId: state => state.guest.issuance_request_id,
    }),
    password: {
      get() {
        return this.$store.state.issuance_request_download.password;
      },
      set(value) {
        this.$store.commit('issuance_request_download/setPassword', {value});
      },
    },
    isValidPeriod() {
      // ダウンロード有効期限チェック
      return this.$moment(this.$moment().format('YYYY-MM-DD'), 'YYYY-MM-DD').isSameOrBefore(this.issue_expire_date);
    },
    reissueRouteName() {
      return this.isGuest ? 'GuestReissuanceRequestRegister' : 'ReissuanceRequestRegister';
    },
    issuanceRequestId() {
      return this.isGuest ? this.guestIssuanceRequestId : Number(this.$route.params.id);
    },
    isStatusReissueRequest() {
      return this.status === this.$constant.ISSUANCE_REQUEST_STATUS.REISSUANCE_REQUEST.code;
    },
    canDownload() {
      return (this.status === this.$constant.ISSUANCE_REQUEST_STATUS.ISSUANCE_COMPLETED.code
          || this.status === this.$constant.ISSUANCE_REQUEST_STATUS.DOWNLOAD_COMPLETED.code)
          && this.issues.length >= 1;
    },
    canReissue() {
      // 再発行可能期間チェック
      if (this.isValidPeriod) {
        // ダウンロード有効期限内
        return true;
      } else {
        // ダウンロード有効期限外
        return this.reissuePossibleType === this.$constant.REISSUE_POSSIBLE_TYPE.ALWAYS.code;
      }
    },
    canDownloadReciept() {
      return this.isGuest
        && this.use_receipt
        && this.payment_method === 0
        && this.billing_price !== 0
        && (this.status === this.$constant.ISSUANCE_REQUEST_STATUS.ISSUING.code
            || this.status === this.$constant.ISSUANCE_REQUEST_STATUS.ISSUANCE_COMPLETED.code
            || this.status === this.$constant.ISSUANCE_REQUEST_STATUS.DOWNLOAD_COMPLETED.code
            || this.status === this.$constant.ISSUANCE_REQUEST_STATUS.REISSUANCE_REQUEST.code)
        && (this.receipt_issued_status === this.$constant.RECEIPT_STATUS.RECEIPT_STATUS_UNISSUED);
    },
    canRedownloadReciept() {
      return this.isGuest
        && this.use_receipt
        && this.payment_method === 0
        && this.billing_price !== 0
        && (this.status === this.$constant.ISSUANCE_REQUEST_STATUS.ISSUING.code
            || this.status === this.$constant.ISSUANCE_REQUEST_STATUS.ISSUANCE_COMPLETED.code
            || this.status === this.$constant.ISSUANCE_REQUEST_STATUS.DOWNLOAD_COMPLETED.code
            || this.status === this.$constant.ISSUANCE_REQUEST_STATUS.REISSUANCE_REQUEST.code)
        && (this.receipt_issued_status === this.$constant.RECEIPT_STATUS.RECEIPT_STATUS_REISSUE_PERMIT);
    },
    canRequestReissueReceipt() {
      return this.isGuest
        && this.use_receipt
        && this.payment_method === 0
        && this.billing_price !== 0
        && (this.status === this.$constant.ISSUANCE_REQUEST_STATUS.ISSUING.code
            || this.status === this.$constant.ISSUANCE_REQUEST_STATUS.ISSUANCE_COMPLETED.code
            || this.status === this.$constant.ISSUANCE_REQUEST_STATUS.DOWNLOAD_COMPLETED.code
            || this.status === this.$constant.ISSUANCE_REQUEST_STATUS.REISSUANCE_REQUEST.code)
        && (this.receipt_issued_status === this.$constant.RECEIPT_STATUS.RECEIPT_STATUS_ISSUED
            ||this.receipt_issued_status === this.$constant.RECEIPT_STATUS.RECEIPT_STATUS_REISSUE_NG
            ||this.receipt_issued_status === this.$constant.RECEIPT_STATUS.RECEIPT_STATUS_REISSUE_DONE);
    },
  },
  methods: {
    async loadPage() {
      if (this.isGuest){
        await this.$store.dispatch('account/authGuest', {
          access_key: sessionStorage.getItem('accessKey')
        }).catch((reason) => this.$root.failApi(reason));
      }
      // ゲストの場合は認証時に発行依頼IDを取得済み
      return this.$store.dispatch('issuance_request_download/load', {id: this.issuanceRequestId})
        .then(() => {
            this.$root.showFlashMessage();
          })
        .catch((reason) => this.$root.failApi(reason));
    },
    async doDownload(payload) {
      if (this.isGuest){
        await this.$store.dispatch('account/authGuest', {
          access_key: sessionStorage.getItem('accessKey')
        }).catch((reason) => this.$root.failApi(reason));
      }
      this.$store.dispatch('issuance_request_download/submit', payload).catch(reason => this.$root.failApi(reason));
    },
    toViewer() {
      this.$router.go(-1);
    },
    setReceiptReason(value) {
      this.receiptReason = value;
    },
    doReceiptRequest() {
      this.updateStatusHandler(this.operation, this.receiptReason);
    },
    canBack() {
      // 画面遷移して起動した場合true
      if (this.$route.params.showBackButton) {
        return JSON.parse(this.$route.params.showBackButton);
      }
      return false;
    },
    getPrintPreviewUrl() {
      let resolvedRoute = this.$router.resolve({
        name: 'ReceiptIssue',
        params: {id: this.id}
      });
      return resolvedRoute.href;
    },
    updateStatusHandler(payload, reason = null) {
      // 再発行の場合、理由をモーダルで確認
      if (payload === this.$constant.RECEIPT_STATUS.RECEIPT_STATUS_REISSUE_REQUEST && reason === null) {
        // operationをセット
        this.operation = payload;
        this.$refs.confirmReceiptModal.show();
      } else {
        this.$store.dispatch('issuance_request_download/updateReceiptStatus', {
          id: this.id,
          value: payload,
          reason: reason,
        }).then(() => {
          // 領収書再発行依頼の場合のみメッセージを表示し、画面を上部にスクロール
          if (payload === this.$constant.RECEIPT_STATUS.RECEIPT_STATUS_REISSUE_REQUEST) {
            this.$store.commit('setFlashMessage', { value: '領収書再発行依頼を行いました' }, { root: true });
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
          this.loadPage();
        });
      }
    }
  }
}
</script>
